import { useState, useMemo, useCallback, useEffect } from "react";
import Select, { components } from "react-select";
import { RandomImage } from "..";
import logger from "../../utils/logger";
import { Badge } from "rsuite";
import styles from "./styles.jsx";
import { IoArrowDownCircle, IoArrowForwardCircle } from "react-icons/io5";

import "./select.picker.css";

function SelectPicker(props) {
  // Track which groups are collapsed. Default to collapsed when groupBy is used.
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(props.defaultValue);
  const {
    valueKey = "value",
    labelKey = "label",
    defaultValue,
    data = [],
    sortBy,
    disabled,
    forwardRef,
    rightDataField,
    onChange,
    style,
    isMulti,
    groupBy,
    groupByLabel,
    expanded,
    itemIcon,
    testid,
    className,
    loading,
    noOptionsMessage,
    filterOption: externalFilterOption,
    getOptionLabel: externalGetOptionLabel,
    ...otherProps
  } = props;

  // Support both `testid` prop and `data-testid` attribute passed from callers.
  // Some call sites pass `data-testid` (hyphenated) while others pass `testid`.
  const effectiveTestId = testid || otherProps["data-testid"] || "select-picker";

  const toggleGroupCollapse = (group) => {
    setCollapsedGroups((prevState) => ({
      ...prevState,
      [group]: !prevState[group],
    }));
  };

  // Sync selectedValue with value prop changes
  useEffect(() => {
    if (props.value !== undefined) {
      // Only sync if the value has actually changed
      if (isMulti) {
        // For multi-select, check if it's a different array
        const isArrayEqual = Array.isArray(selectedValue) && Array.isArray(props.value) &&
          selectedValue.length === props.value.length &&
          selectedValue.every((item, idx) => item[valueKey] === props.value[idx][valueKey]);
        if (!isArrayEqual) {
          const matchedOption = data.filter((item) => props.value && props.value.includes(item[valueKey]));
          setSelectedValue(matchedOption.length > 0 ? matchedOption : null);
        }
      } else {
        // For single select, only update if different
        if (!selectedValue || selectedValue[valueKey] !== props.value) {
          const matchedOption = data.find((d) => d[valueKey] === props.value);
          setSelectedValue(matchedOption || null);
        }
      }
    }
  }, [props.value, data, isMulti, valueKey]);

  // Initialize collapsed groups based on data and groupBy
  const initialCollapsedGroups = useMemo(() => {
    if (!groupBy || !data.length) return {};
    const grouped = data.reduce((acc, option) => {
      const key = option[groupBy];
      if (!acc[key]) acc[key] = [];
      acc[key].push(option);
      return acc;
    }, {});
    return Object.keys(grouped).reduce((acc, key) => {
      if (!acc[key]) acc[key] = !expanded; // default collapsed
      return acc;
    }, {});
  }, [data, groupBy, expanded]);

  // Only update if different to avoid re-renders
  useEffect(() => {
    const keys = Object.keys(initialCollapsedGroups);
    const existingKeys = Object.keys(collapsedGroups);
    if (keys.length !== existingKeys.length || keys.some((k) => collapsedGroups[k] !== initialCollapsedGroups[k])) {
      setCollapsedGroups(initialCollapsedGroups);
    }
  }, [initialCollapsedGroups]);

  const sortOptions = useCallback((options, sortBy) => {
    return options.sort((a, b) => {
      if (!a[sortBy] && !b[sortBy]) return 0;
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      const extractNumber = (str) => {
        if (typeof str === "number") return str;
        const s = String(str || "");
        const match = s.match(/BESTSTART (\d+)/);
        return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
      };

      const aNum = extractNumber(aValue || a.label);
      const bNum = extractNumber(bValue || b.label);

      if (aNum < bNum) {
        return -1;
      }
      if (aNum > bNum) {
        return 1;
      }

      // If the numbers are equal, compare the full strings
      const aStr = aValue || a.label;
      const bStr = bValue || b.label;

      if (aStr < bStr) {
        return -1;
      }
      if (aStr > bStr) {
        return 1;
      }

      return 0;
    });
  }, []);

  const options = useMemo(() => {
    let sortedOptions = data;
    if (groupBy) {
      const groupedOptions = data.reduce((acc, option) => {
        const value = option[groupBy];
        acc[value] = acc[value] || [];
        acc[value].push(option);
        return acc;
      }, {});

      sortedOptions = Object.keys(groupedOptions).map((item) => ({
        label: item,
        options: sortBy ? sortOptions(groupedOptions[item], sortBy) : groupedOptions[item],
      }));
    } else {
      sortedOptions = sortBy ? sortOptions(data, sortBy) : data;
    }
    return sortedOptions;
  }, [data, groupBy, sortBy, labelKey, sortOptions]);

  const filterOption = useCallback(
    (option, rawInput) => {
      const words = rawInput.split(" ").map((word) => word.toLowerCase());
      const optionLabel = `${option.data[labelKey]}`.toLowerCase();
      const groupByLabel = `${option.data[groupBy]}`.toLowerCase();
      return words.every((word) => optionLabel.includes(word) || groupByLabel.includes(word));
    },
    [groupBy]
  );

  const formatGroupLabel = useCallback(
    (data) => {
      const isCollapsed = collapsedGroups[data.label];

      return (
        <div
          data-testid={`${data.label}-test`}
          style={{
            overflow: "hidden",
            height: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            padding: "8px 12px",
            borderRadius: "4px",
            backgroundColor: "var(--secondary-color)",
            color: "white",
            borderBottom: "none"
          }}
          onClick={() => {
            toggleGroupCollapse(data.label);
            setMenuOpen(true);
          }}
        >
          <span style={{ fontSize: "1.5em", fontWeight: "bolder", display: "flex", alignItems: "center" }}>
            {isCollapsed ? (
              <IoArrowForwardCircle style={{ height: 30, width: 30, marginRight: 10 }} />
            ) : (
              <IoArrowDownCircle style={{ height: 30, width: 30, marginRight: 10 }} />
            )}
            {data.label}
          </span>
          <span>
            <RandomImage seed={data.label} height={50} className="group-label-image" />
            <Badge content={data.options.length} style={{ backgroundColor: "var(--primary-color)" }}></Badge>
          </span>
        </div>
      );
    },
    [collapsedGroups, groupByLabel]
  );

  const customItems = useCallback(
    (props) => {
      return (
        <components.Option {...props} className="select-option" aria-label={`${props.children}-test`}>
          <span className="select-option-icon">{itemIcon ? itemIcon : ""}</span>
          <span className="select-option-text">{props.children}</span>
        </components.Option>
      );
    },
    [itemIcon]
  );

  const customGroup = useCallback(
    (props) => {
      const isCollapsed = collapsedGroups[props.data.label];
      return <components.Group className="select-group" {...props}>{props.children && !isCollapsed ? props.children : null}</components.Group>;
    },
    [collapsedGroups]
  );

  const onValueChange = useCallback(
    (val) => {
      setSelectedValue(val);
      if (!onChange) {
        logger.warn("onChange is not defined");
        return;
      }

      onChange(val);

      setMenuOpen(isMulti);
    },
    [onChange, isMulti, valueKey]
  );

  const CustomMultiValue = (props) => {
    return (
      <div data-testid={`${props.data[valueKey]}-multi-value`} className="custom-multi-value">
        <components.MultiValue {...props} />
      </div>
    );
  };

  const defaultVal = useMemo(() => {
    if (data.length === 0) return null;
    const defaultVal = isMulti
      ? data.filter((item) => defaultValue && defaultValue.includes(item[valueKey]))
      : data.find((d) => d[valueKey] === defaultValue);
    setSelectedValue(defaultVal);
    return Array.isArray(defaultVal) ? (defaultVal.length > 0 ? defaultVal : null) : defaultVal;
  }, [data, isMulti, defaultValue, valueKey]);

  // Merge default styles with user-provided styles and ensure menuPortal has a high z-index
  const mergedStyles = useMemo(() => {
    const baseStyles = { ...styles, ...(style || {}) };
    const userMenuPortal = baseStyles.menuPortal;
    baseStyles.menuPortal = (base, state) => {
      const userResult = typeof userMenuPortal === "function" ? userMenuPortal(base, state) : userMenuPortal || {};
      return { ...base, zIndex: 999999, ...userResult };
    };
    return baseStyles;
  }, [style]);

  return (
    <Select
      {...otherProps}
      ref={forwardRef}
      className={`select-picker-master ${className || ""}`}
      classNamePrefix="select-picker"
      isLoading={loading}
      data-testid={effectiveTestId}
      isMulti={isMulti}
      filterOption={externalFilterOption || filterOption}
      components={{ Group: customGroup, Option: customItems, MultiValue: CustomMultiValue }}
      /* Render the menu into document.body so it floats above modals and doesn't cause modal scrolling */
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition={"fixed"}
      styles={mergedStyles}
      captureMenuScroll={false}
      getOptionValue={(option) => option?.[valueKey]}
      getOptionLabel={externalGetOptionLabel || ((option) => {
        if (!option) return null;
        return (
          <span style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
            <span>{option[labelKey]}</span>
            <span>{option[rightDataField]}</span>
          </span>
        );
      })}
      options={options}
      onChange={onValueChange}
      menuIsOpen={menuOpen}
      onMenuOpen={() => setMenuOpen(true)}
      onMenuClose={() => setMenuOpen(false)}
      closeMenuOnSelect={!isMulti}
      defaultValue={defaultVal}
      value={selectedValue}
      formatGroupLabel={formatGroupLabel}
      isDisabled={disabled}
      noOptionsMessage={noOptionsMessage}
    />
  );
}

export default SelectPicker;
